import * as Blockly from 'blockly/core';
import { BT_NAME_ANNOUNCE_PREFIX } from '@/constants/constants';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import {
    registerHelperFunction,
    registerLoopCode,
    registerSetupCode,
} from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import { markBtUsed } from '@/(routers)/boards/editor/block/generators/arduino/blocks/serial/helper/monitor_bluetooth_helper';

const SEPARATOR_MAP: Record<string, string> = {
    NONE: '',
    COMMA: ', ',
    SPACE: ' ',
    TAB: '\t',
};

export function register(gen: any): void {
    gen.forBlock['monitor_bluetooth'] = function (block: Blockly.Block): string {
        markBtUsed();
        // BT名は仮想COMポート番号が接続の都度変わる中でボードを一意に特定するための固定識別子。
        // 以前は実機のloop()前でchip_id(efuse MAC由来)からsprintfで実行時計算していたが、
        // 2026-07時点でfox側は書き込み前に必ずROM経由でchip_id/boardIdentityを解決済みのため、
        // その場で確定しているリテラル文字列をそのままソースコードに埋め込む方式に変更した。
        // (バックエンドのboards.py:_compute_bluetooth_device_nameと同じ値・書式)
        const { chipId, boardIdentity } = useBoardEditorStore.getState();
        const resolvedBtName = boardIdentity?.bluetooth_device_name;

        // Web Serial APIはBluetooth仮想COMポートのデバイス名を一切公開しない(ブラウザが
        // フィンガープリンティング対策で意図的に隠している)ため、ブリッジ無しでモニターが
        // 「今どの実機に繋がっているか」を知る手段がない。実機自身にこのプレフィックス付きの
        // 行を送らせて名乗ってもらう方式で代替する(フロント側はuseMonitorWebSerialApi.tsで
        // このプレフィックスを検出し、通常のログには出さず接続デバイス名として扱う)。
        // setup()時点では相手先(PC)がまだRFCOMM接続していないことが多く(ボードは起動済みで
        // 後からブラウザ側が接続しにいく運用のため)、その1回だけの送信は届かず消えてしまう。
        // hasClient()で接続確立を検知しつつloop内で定期的に再送することで、ブラウザ側が
        // 後から接続してもすぐに拾えるようにする。
        const SENTINEL = BT_NAME_ANNOUNCE_PREFIX;

        let btName: string;
        let beginCode: string;
        if (resolvedBtName) {
            btName = resolvedBtName;
            beginCode = `  SerialBT.begin("${resolvedBtName}");\n`;
        } else if (chipId != null) {
            // boardIdentity未解決(DB照会失敗/未登録)だがchip_id自体は解決済みの場合、
            // 旧形式(cd部分省略)をその場で計算して埋め込む。
            const hex = chipId.toString(16).toUpperCase().padStart(8, '0');
            btName = `SchooMy_BT_${hex}`;
            beginCode = `  SerialBT.begin("${btName}");\n`;
        } else {
            btName = '';
            beginCode = '';
        }

        // loop()本体に上記の処理をそのまま展開すると、ユーザーが書いたブロックの並びの中に
        // 無関係な内部処理が生で混ざって見えてしまい読みにくい。専用の名前付き関数として
        // 別出しし、loop()側は1行の呼び出しだけにする(percentHelperCode等、既存のヘルパー
        // 関数群と同じ置き場)。
        let setupCode: string;
        let helperFunctionCode: string;
        if (btName) {
            setupCode = beginCode;
            helperFunctionCode =
                'void schoomyAnnounceBtName() {\n' +
                '  static unsigned long lastBtNameAnnounceMs = 0;\n' +
                '  if (SerialBT.hasClient() && millis() - lastBtNameAnnounceMs > 2000) {\n' +
                `    SerialBT.println("${SENTINEL}${btName}");\n` +
                '    lastBtNameAnnounceMs = millis();\n' +
                '  }\n' +
                '}\n';
        } else {
            // chip_id自体も未解決な稀なケース(問い合わせ失敗直後の書き込み等)への保険として、
            // 実行時に自前で計算する旧来のフォールバックコードを維持する。setup()内のbtName
            // (ローカル変数)はこの関数から見えないため、独立してもう一度計算する
            // (同じefuse MAC由来なので毎回同じ文字列になり、値がずれる心配はない)。
            setupCode =
                '  char btName[24];\n' +
                '  sprintf(btName, "SchooMy_BT_%08lX", (unsigned long)scmUtils.getChipId(ESP.getEfuseMac()).toInt());\n' +
                '  SerialBT.begin(btName);\n';
            helperFunctionCode =
                'void schoomyAnnounceBtName() {\n' +
                '  static char loopBtName[24] = "";\n' +
                '  static unsigned long lastBtNameAnnounceMs = 0;\n' +
                '  if (loopBtName[0] == \'\\0\') {\n' +
                '    sprintf(loopBtName, "SchooMy_BT_%08lX", (unsigned long)scmUtils.getChipId(ESP.getEfuseMac()).toInt());\n' +
                '  }\n' +
                '  if (SerialBT.hasClient() && millis() - lastBtNameAnnounceMs > 2000) {\n' +
                `    SerialBT.print("${SENTINEL}");\n` +
                '    SerialBT.println(loopBtName);\n' +
                '    lastBtNameAnnounceMs = millis();\n' +
                '  }\n' +
                '}\n';
        }
        registerSetupCode('bluetooth_begin', setupCode);
        registerHelperFunction('bluetooth_name_announce', helperFunctionCode);
        registerLoopCode('bluetooth_name_announce', '  schoomyAnnounceBtName();\n');
        const liveIds = block.inputList
            .filter((i) => i.name.startsWith('VAL_'))
            .map((i) => i.name.replace('VAL_', ''));
        const sep = SEPARATOR_MAP[block.getFieldValue('SEPARATOR')] ?? '';
        const newline = (block as any)._newlineValue ?? block.getFieldValue('NEWLINE') ?? 'ALL';
        const values = liveIds.map((id) => gen.valueToCode(block, `VAL_${id}`, 0) || '0');
        const lines: string[] = [];
        values.forEach((v, i) => {
            const isLast = i === values.length - 1;
            const useNewline = newline === 'ALL' || (newline === 'LAST' && isLast);
            lines.push(`SerialBT.${useNewline ? 'println' : 'print'}(${v});`);
            // ALLモードは値ごとに改行されるため、値同士を同一行上で区切る区切り文字は出力不要
            // (出さないと値と区切り文字がそれぞれ別々の行に分断されてしまう)。
            if (!isLast && sep !== '' && newline !== 'ALL') {
                lines.push(`SerialBT.print("${sep}");`);
            }
        });
        return lines.join('\n') + '\n';
    };
}
